import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// Internals
import { TODO } from '../types';
import Typography from '../components/Typography';
import TableCard from '../components/TableCard';
import { truncate } from '../utils';
import EmptyState from '../components/EmptyState';

// Antd
import {
  Button,
  Divider,
  PageHeader,
  Tag,
  Spin,
  Form,
  Space,
  Modal,
  Select,
  notification,
  Progress,
  message,
} from 'antd';

// Antd date-fns replacement
// https://ant.design/docs/react/replace-moment#Use-date-fns
import dateFnsGenerateConfig from 'rc-picker/lib/generate/dateFns';
import generatePicker from 'antd/es/date-picker/generatePicker';

// Icons
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ChevronsRight,
  Download,
  ExternalLink,
  EyeOff,
  Plus,
  Repeat,
  Search,
  Settings,
} from 'react-feather';

import { Category } from '../types';

// Firebase
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../services/firebase';

// Styles
import './transactions.scss';

// MISC
import axios from 'axios';
import { get } from 'lodash-es';
import { endOfMonth, format, startOfMonth, startOfWeek, startOfYear, sub } from 'date-fns';
import useApi from '../hooks/useApi';
import useOrg from '../hooks/useOrg';
import { BigNumber } from 'ethers';
import { formatUnits } from 'ethers/lib/utils';
import { downloadCSV } from '../utils/downloadCSV';

const DatePicker = generatePicker<Date>(dateFnsGenerateConfig);

const ICONS = {
  deposit: {
    main: <ArrowDown />,
    progress: <ArrowRight />,
  },
  exchange: {
    main: <Repeat />,
    progress: <Repeat />,
  },
  trade: {
    main: <Repeat />,
    progress: <Repeat />,
  },
  transfer: {
    main: <ChevronsRight />,
    progress: <ChevronsRight />,
  },
  withdrawal: {
    main: <ArrowUp />,
    progress: <ArrowLeft />,
  },
} as any;

const { Option } = Select;
const RangePicker: any = DatePicker.RangePicker;

const columns = [
  {
    title: 'Date',
    dataIndex: 'timestamp',
    key: 'timestamp',
    render: (datum: TODO, record: any, i: any) => {
      const timestampDate = new Date(record.timestamp);

      return (
        <div style={{ minWidth: '150px' }}>
          <Space>
            <div className={`item-type ${record.label.toLowerCase()}`}>
              {ICONS[record.label.toLowerCase()]?.main}
            </div>

            <div>
              <Typography type="text_sm" weight="medium">
                {record.label}
              </Typography>

              <Typography type="text_sm" color="secondary">
                {format(timestampDate, 'MMM dd - ')}
                {(timestampDate.getHours() < 10 ? '0' : '') + timestampDate.getHours()}:
                {(timestampDate.getMinutes() < 10 ? '0' : '') + timestampDate.getMinutes()}
              </Typography>
            </div>
          </Space>
        </div>
      );
    },
    sorter: (a: any, b: any) => +new Date(a.timestamp) - +new Date(b.timestamp),
    sortDirections: ['ascend', 'descend'],
  },
  {
    title: 'Counterparty',
    dataIndex: 'toName',
    key: 'toName',
    render: (datum: TODO, record: any, i: any) => {
      return (
        <>
          <Typography type="text_sm">{datum}</Typography>

          <Typography type="text_sm" color="secondary">
            {truncate(record.toAddress)}
          </Typography>
        </>
      );
    },
  },
  {
    title: '',
    dataIndex: 'progress',
    key: 'progress',
    render: (datum: TODO, record: any, i: any) => {
      return (
        <div className={`item-progress ${record.label.toLowerCase()}`}>
          {ICONS[record.label.toLowerCase()]?.progress}
        </div>
      );
    },
  },
  {
    title: 'Internal wallet',
    dataIndex: 'fromName',
    key: 'fromName',
    render: (datum: TODO, record: any, i: any) => {
      return (
        <div style={{ minWidth: '150px' }}>
          <Typography type="text_sm">{datum}</Typography>

          <Typography type="text_sm" color="secondary">
            {truncate(record.fromAddress)}
          </Typography>
        </div>
      );
    },
  },
  {
    title: 'Amount',
    dataIndex: 'usdAmount',
    key: 'usdAmount',
    render: (datum: TODO, record: any, i: any) => {
      const parsedFloat = parseFloat(datum);

      return (
        <>
          <Typography type="text_sm">
            {!isNaN(parsedFloat) &&
              (record.type.toLowerCase() === 'withdrawal' && parsedFloat !== 0 ? (
                <>({parsedFloat.toFixed(2)} USD)</>
              ) : (
                <>{parsedFloat.toFixed(2)} USD</>
              ))}
          </Typography>

          <Typography type="text_sm" color="secondary">
            {record.amount &&
              (record.type.toLowerCase() === 'withdrawal' && parsedFloat !== 0 ? (
                <>
                  ({formatUnits(BigNumber.from(record.amount), record.decimal)} {record.symbol})
                </>
              ) : (
                <>
                  {formatUnits(BigNumber.from(record.amount), record.decimal)} {record.symbol}
                </>
              ))}
          </Typography>
        </>
      );
    },
  },
  {
    title: 'Fee',
    dataIndex: 'feeUsdAmount',
    key: 'feeUsdAmount',
    render: (datum: TODO, record: any, i: any) => {
      return (
        <div style={{ minWidth: '100px' }}>
          <Typography type="text_sm" color="secondary">
            {datum && <>$({datum})</>}
          </Typography>
        </div>
      );
    },
  },
  {
    title: 'Memo',
    dataIndex: 'memo',
    key: 'memo',
    render: (datum: TODO, record: any, i: any) => {
      return (
        <div style={{ minWidth: '200px' }}>
          <Typography type="text_sm" color="secondary">
            {datum || '__'}
          </Typography>
        </div>
      );
    },
    className: 'cell-memo',
  },
  {
    title: 'Actions',
    dataIndex: 'priceSource',
    key: 'priceSource',
    render: (datum: TODO, record: any, i: any) => {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {/* <Button shape="circle" icon={<EyeOff />} /> */}

          <a target="_blank" href={'https://etherscan.io/tx/' + record.txnHash} rel="noreferrer">
            <Button shape="circle" icon={<ExternalLink />} />
          </a>
        </div>
      );
    },
    fixed: 'right',
    className: 'cell-actions',
  },
];

export default function Transactions() {
  const [categories, setCategories] = useState<Array<Category>>([]);
  const api = useApi();

  useEffect(() => {
    if (api) {
      api
        .getCategories()
        .then((res) => {
          setCategories(res);
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [api]);

  const [user, loading, error] = useAuthState(auth);
  const [apiData, setApiData] = React.useState<Array<any> | null>(null);

  React.useEffect(() => {
    if (user && get(user, 'accessToken')) {
      fetch(process.env.REACT_APP_API_URL + '/transactions', {
        headers: new Headers({
          Authorization: 'Basic ' + (user as any).accessToken,
          'Content-Type': 'application/json',
        }),
      })
        .then((response) => response.json())
        .then(({ data }) => {
          setApiData(data);
        })
        .catch((e) => {
          console.log(e);

          message.error('Something went wrong');
        });
    }
  }, []);

  const [modalVisible, setModalVisible] = React.useState(false);
  return (
    <>
      <PageHeader
        ghost={false}
        title={
          <>
            <Typography type="display_sm" weight="medium">
              Transactions
            </Typography>

            <Typography type="text_md" color="secondary">
              View a full history of all your transactions across all accounts.
            </Typography>
          </>
        }
        extra={
          apiData !== null && apiData.length !== 0
            ? [
                <Button key="1" type="primary" size="large" onClick={() => setModalVisible(true)}>
                  <Download /> Export to CSV
                </Button>,
              ]
            : [
                <Link to="/accounts/add">
                  <Button key="2" type="primary" size="large">
                    <Plus /> Add account
                  </Button>
                </Link>,
              ]
        }
      />

      <Divider />

      <div className="transactions__table-card-container">
        <Spin spinning={apiData === null}>
          {Array.isArray(apiData) && apiData.length !== 0 && (
            <TableCard
              categories={categories}
              checkboxSelection={true}
              dataSource={apiData}
              columns={columns}
              tableRowKey="id"
              title={<>All Transactions</>}
            />
          )}

          {Array.isArray(apiData) && apiData.length === 0 && (
            <EmptyState
              title="There are no transactions here"
              subTitle="Check the connection of your accounts or connect wallets, custodians, exchanges, and accounting software to see the magic happen!"
              action={
                <Link to="/accounts/add">
                  <Button type="primary" size="large">
                    <Plus /> Add account
                  </Button>
                </Link>
              }
              icon={<Search />}
            />
          )}
        </Spin>
      </div>

      <ModalForm modalVisible={modalVisible} setModalVisible={setModalVisible} />
    </>
  );
}

interface ModalFormProps {
  modalVisible: boolean;
  setModalVisible: Function;
}

interface Account {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  address: string;
}

interface ExportRequestBody {
  start: Date;
  end: Date;
  accounts: Array<string>;
  labels: Array<string>;
  symbols: Array<string>;
}

const ModalForm = ({ modalVisible, setModalVisible }: ModalFormProps) => {
  const [user] = useAuthState(auth);
  const org = useOrg();

  const [accounts, setAccounts] = useState<Array<Account>>([]);
  const [assets, setAssets] = useState<Array<string>>([]);

  const [csvPercent, setCsvPercent] = useState(0);

  const [notificationKey, setNotificationKey] = useState('');
  const axiosClient = axios.create({
    baseURL: 'http://localhost:8000',
  });

  // Initial form values
  const [form] = Form.useForm();
  form.setFieldsValue({
    accounts: 'all',
    assets: 'all',
    transactions: 'all',
  });

  const onFinish = async (values: any) => {
    console.log('values', values);

    const reqBody: ExportRequestBody = {
      start: values.timeframe[0],
      end: values.timeframe[1],
      accounts: [],
      labels: [values.transactions],
      symbols: [values.assets],
    };

    if (values.accounts === 'all') {
      reqBody.accounts = accounts.map((account) => account.id);
    } else {
      let account = accounts.find((acc) => acc.name === values.accounts);
      reqBody.accounts.push(account?.id || '');
    }

    if (values.assets === 'all') {
      reqBody.symbols = assets;
    }

    if (values.transactions === 'all') {
      reqBody.labels = ['Deposit', 'Withdrawal', 'Transfer'];
    }

    // Hide modal
    setModalVisible(false);

    // Show notification
    const key = `open${Date.now()}`;
    setNotificationKey(key);
    notification.open({
      message: 'Exporting Transactions',
      description: (
        <>
          <p>Generating data</p>

          <Progress percent={csvPercent} style={{ marginTop: '1rem' }} />
        </>
      ),
      key,
      duration: null,
    });

    await axiosClient
      .post('/transactions/export', reqBody, {
        headers: {
          Authorization: `Bearer ${await user?.getIdToken()}`,
        },
        onDownloadProgress: (progressEvent) => {
          let percentCompleted = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
          setCsvPercent(percentCompleted);
        },
      })
      .then((res) => {
        const csvContent = res.data.csv ?? '';
        if (csvContent) {
          const filename = `integral-export-${org.name}`;

          downloadCSV(csvContent, filename);
        } else {
          message.error('Something went wrong');
        }
      })
      .catch((err) => {
        console.log({ err });

        message.error('Something went wrong');
      });
  };

  const fetchAccounts = async () => {
    axiosClient
      .get('/accounts/', {
        headers: {
          Authorization: `Bearer ${await user?.getIdToken()}`,
        },
      })
      .then((res) => {
        setAccounts(res.data.data);
      })
      .catch((err) => {
        console.log({ err });
      });
  };

  const fetchAssets = async () => {
    axiosClient
      .get('/transactions/assets/', {
        headers: {
          Authorization: `Bearer ${await user?.getIdToken()}`,
        },
      })
      .then((res) => {
        setAssets(res.data.data);
      })
      .catch((err) => {
        console.log({ err });
      });
  };

  useEffect(() => {
    fetchAccounts();
    fetchAssets();
  }, []);

  useEffect(() => {
    if (csvPercent === 100) {
      notification.close(notificationKey);
      setNotificationKey('');
      setCsvPercent(0);
    }
  }, [csvPercent]);

  return (
    <Modal
      title="Export transactions"
      visible={modalVisible}
      onOk={() => form.submit()}
      onCancel={() => setModalVisible(false)}
    >
      <Form form={form} name="control-hooks" onFinish={onFinish} layout="vertical">
        <Form.Item name="timeframe" label="Timeframe" rules={[{ required: true }]}>
          <RangePicker
            style={{ width: '100%' }}
            ranges={{
              'This Month': [startOfMonth(new Date()), new Date()] as any,
              'Last Month': [
                startOfMonth(
                  sub(new Date(), {
                    months: 1,
                  }),
                ),
                endOfMonth(
                  sub(new Date(), {
                    months: 1,
                  }),
                ),
              ] as any,
              'This Week': [startOfWeek(new Date()), new Date()] as any,
              'This Year': [startOfYear(new Date()), new Date()] as any,
            }}
          />
        </Form.Item>

        <Form.Item name="accounts" label="Accounts" rules={[{ required: true }]}>
          <Select>
            <Option value="all">All accounts</Option>
            {accounts.map((account) => (
              <Option value={account.id}>{account.name}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="assets" label="Assets" rules={[{ required: true }]}>
          <Select>
            <Option value="all">All assets</Option>
            {assets.map((asset) => (
              <Option value={asset}>{asset}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="transactions" label="Transaction types" rules={[{ required: true }]}>
          <Select>
            <Option value="all">All transactions</Option>
            <Option value="Deposit">Deposits</Option>
            <Option value="Withdrawal">Withdrawals</Option>
            <Option value="Transfer">Transfers</Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};
